import React, { SyntheticEvent, useCallback, useState } from 'react';
import { TooltipText, IconButtonGroup } from '@app/core';
import { useTranslation } from 'react-i18next';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import classnames from 'classnames';
import { LogicJumpOption } from './interface';
import { LogicChoiceSelector } from './LogicChoiceSelector';
import { QuestionNumber } from './QuestionNumber';
import { StopIconWithBg } from './StopIconWithBg';
import { LogicJumpActionType } from 'queries';

const useStyles = createUseStyles((theme: Theme) => ({
  iconAndNumberContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: `${theme.marginBase / 2}px`,
  },
  icon: {
    color: theme.colors.inkSecondary,
  },
}));

interface Props {
  onChange?: (newOption: LogicJumpOption, choiceRef: string) => any;
  logicJumpOptions?: Array<LogicJumpOption>;
  selectedLogicJumpOption?: LogicJumpOption;
  choiceRef: string;
  isLastField?: boolean;
}

export const LogicButton = ({
  onChange,
  logicJumpOptions,
  selectedLogicJumpOption,
  choiceRef,
  isLastField,
}: Props) => {
  const { t } = useTranslation();
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });

  const [anchorElmnt, setAnchorElmnt] = useState<null | Element>(null);

  const onClick = (event: SyntheticEvent) => {
    event.stopPropagation();
    setAnchorElmnt(event.currentTarget);
  };

  const onClose = useCallback(() => {
    setAnchorElmnt(null);
  }, []);

  const onChoiceSelect = useCallback(
    (option: LogicJumpOption) => {
      if (!onChange) {
        return;
      }
      onChange(option, choiceRef);
    },
    [onChange, choiceRef],
  );

  if (!onChange || !logicJumpOptions) {
    return null;
  }

  const isEndFormSelected =
    selectedLogicJumpOption &&
    (selectedLogicJumpOption.type === LogicJumpActionType.END_FORM ||
      (selectedLogicJumpOption.type === LogicJumpActionType.NEXT_QUESTION &&
        isLastField));

  return (
    <div>
      <TooltipText
        title={t('screeningForm.create.questionBuilder.choiceList.logicButton')}
      >
        <IconButtonGroup
          small
          onClick={onClick}
          aria-label={t(
            'screeningForm.create.questionBuilder.choiceList.logicButton',
          )}
        >
          <div className={classes.iconAndNumberContainer}>
            <i className={classnames('ri-guide-line', classes.icon)} />
            {selectedLogicJumpOption?.number && (
              <QuestionNumber number={selectedLogicJumpOption.number} />
            )}
            {isEndFormSelected && <StopIconWithBg isRoundedSquare />}
          </div>
        </IconButtonGroup>
      </TooltipText>
      <LogicChoiceSelector
        anchorElmnt={anchorElmnt}
        onChange={onChoiceSelect}
        logicJumpOptions={logicJumpOptions}
        selectedLogicJumpOption={selectedLogicJumpOption}
        onClose={onClose}
      />
    </div>
  );
};
